import { useState, useEffect, useRef } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import ReactMarkdown from 'react-markdown';
import {
  X,
  ExternalLink,
  Instagram,
  Youtube,
  BadgeCheck,
  Eye,
  Heart,
  MessageCircle,
} from 'lucide-react';
import { analyzeReelStream, reelMediaUrl } from '../lib/api';
import ComposerModal from './ComposerModal';
import './ReelAnalysisModal.css';

function TikTokGlyph({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M16.5 3c.32 2.13 1.57 3.7 3.66 3.98v2.43c-1.3.13-2.56-.27-3.66-1v5.92a6.05 6.05 0 1 1-6.05-6.05c.31 0 .62.02.92.07v2.62a3.43 3.43 0 1 0 2.51 3.31V3h2.61z" />
    </svg>
  );
}

const PLATFORM_ICONS = { instagram: Instagram, tiktok: TikTokGlyph, youtube: Youtube };
const PLATFORM_LABEL = { instagram: 'Instagram', tiktok: 'TikTok', youtube: 'YouTube' };

function formatCount(value) {
  if (value === null || value === undefined) return null;
  const num = Number(value);
  if (Number.isNaN(num) || num < 0) return null;
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  return `${num}`;
}

const SKELETON_MESSAGES = [
  'Watching the video frame by frame…',
  'Spotting the hook…',
  'Mapping the structure & pacing…',
  'Figuring out why it works…',
];

function AnalysisSkeleton() {
  const [msgIndex, setMsgIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setMsgIndex((i) => (i + 1) % SKELETON_MESSAGES.length);
    }, 2200);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="ra-skeleton" aria-hidden="true">
      <div className="ra-skel-status">
        <span className="ra-skel-dot" />
        {SKELETON_MESSAGES[msgIndex]}
      </div>
      {[0, 1, 2, 3].map((section) => (
        <div className="ra-skel-section" key={section}>
          <div className="ra-skel-heading" />
          <div className="ra-skel-line" />
          <div className="ra-skel-line" />
          <div className="ra-skel-line ra-skel-line--short" />
        </div>
      ))}
    </div>
  );
}

export default function ReelAnalysisModal({ reel, onClose }) {
  const [analysis, setAnalysis] = useState('');
  const [status, setStatus] = useState('streaming'); // streaming | done | error
  const [errorMsg, setErrorMsg] = useState(null);
  const [videoFailed, setVideoFailed] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [composerOpen, setComposerOpen] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (!reel) return undefined;
    setAnalysis('');
    setStatus('streaming');
    setErrorMsg(null);

    const controller = new AbortController();
    analyzeReelStream(reel, {
      signal: controller.signal,
      onToken: (chunk) => setAnalysis((prev) => prev + chunk),
    })
      .then(() => setStatus('done'))
      .catch((err) => {
        if (err.name === 'AbortError') return;
        setErrorMsg('Analysis failed');
        setStatus('error');
      });

    return () => controller.abort();
  }, [reel, retryKey]);

  // Follow the text as it streams in.
  useEffect(() => {
    if (status === 'streaming' && bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [analysis, status]);

  if (!reel) return null;

  const Icon = PLATFORM_ICONS[reel.platform];
  const isYouTube = reel.platform === 'youtube';
  const views = formatCount(reel.view_count);
  const likes = formatCount(reel.like_count);
  const comments = formatCount(reel.comment_count);

  const renderMedia = () => {
    if (isYouTube && reel.shortcode) {
      return (
        <iframe
          className="ra-video"
          src={`https://www.youtube.com/embed/${reel.shortcode}?autoplay=1&mute=1&playsinline=1&rel=0`}
          title="Reel"
          allow="autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
        />
      );
    }
    if (reel.video_url && !videoFailed) {
      return (
        <video
          className="ra-video"
          src={reel.video_url}
          poster={reelMediaUrl(reel.thumbnail_url) || undefined}
          controls
          autoPlay
          loop
          playsInline
          onError={() => setVideoFailed(true)}
        />
      );
    }
    if (reel.thumbnail_url) {
      return <img className="ra-video" src={reelMediaUrl(reel.thumbnail_url)} alt="" />;
    }
    return <div className="ra-video ra-video--placeholder" />;
  };

  return (
    <>
    <Dialog.Root open onOpenChange={(o) => { if (!o) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="ra-overlay" />
        <Dialog.Content className="ra-content" aria-describedby={undefined}>
          <Dialog.Title className="ra-visually-hidden">Reel analysis</Dialog.Title>

          {/* Left: video */}
          <div className="ra-left">{renderMedia()}</div>

          {/* Right: analysis */}
          <div className="ra-right">
            <div className="ra-header">
              <div className="ra-creator">
                {Icon && (
                  <span className={`ra-plat ra-plat--${reel.platform}`}>
                    <Icon size={15} />
                  </span>
                )}
                {reel.username ? (
                  <span className="ra-username">
                    @{reel.username}
                    {reel.is_verified && <BadgeCheck size={14} className="ra-verified" />}
                  </span>
                ) : (
                  <span className="ra-username">{PLATFORM_LABEL[reel.platform] || 'Reel'}</span>
                )}
              </div>
              <div className="ra-actions">
                <button
                  type="button"
                  className="ra-use-composer"
                  onClick={() => setComposerOpen(true)}
                >
                  Use in Composer
                </button>
                <a
                  className="ra-source"
                  href={reel.permalink}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={`Open on ${PLATFORM_LABEL[reel.platform] || 'source'}`}
                  aria-label={`Open on ${PLATFORM_LABEL[reel.platform] || 'source'}`}
                >
                  <ExternalLink size={15} />
                </a>
                <button className="ra-close" onClick={onClose} aria-label="Close">
                  <X size={18} />
                </button>
              </div>
            </div>

            {(views || likes || comments) && (
              <div className="ra-stats">
                {views && <span><Eye size={13} /> {views}</span>}
                {likes && <span><Heart size={13} /> {likes}</span>}
                {comments && <span><MessageCircle size={13} /> {comments}</span>}
              </div>
            )}

            <div className="ra-analysis-title">Analysis</div>

            <div className="ra-analysis-body" ref={bodyRef}>
              {status === 'streaming' && analysis === '' && <AnalysisSkeleton />}

              {analysis && (
                <div className="ra-markdown">
                  <ReactMarkdown>{analysis}</ReactMarkdown>
                  {status === 'streaming' && <span className="ra-caret" />}
                </div>
              )}

              {status === 'error' && (
                <div className="ra-error">
                  <p>{errorMsg || 'Could not generate the analysis.'}</p>
                  <button className="ra-retry" onClick={() => setRetryKey((k) => k + 1)}>
                    Try again
                  </button>
                </div>
              )}
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>

      {composerOpen && (
        <ComposerModal
          reel={reel}
          analysis={analysis}
          onClose={() => setComposerOpen(false)}
          onDismissAll={() => {
            setComposerOpen(false);
            onClose();
          }}
        />
      )}
    </>
  );
}
